import React, { useEffect, useMemo, useState } from "react";
import Layout from "../components/Layout/Layout";
import axios from "axios";
import { useSelector } from "react-redux";
import "./Dashboard.css";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "./components/DashboardLayout";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";

const Dashboard = () => {
  const { user } = useSelector((state) => state.user);
  const navigate = useNavigate();

  const [allOrders, setAllOrders] = useState([]);
  const [loading, setLoading] = useState(false);

  const getAllUserOrders = async () => {
    if (!user?.email) return;

    try {
      setLoading(true);
      const res = await axios.post(
        "/api/order/get-user-orders",
        { email: user.email },
        { headers: { Authorization: "Bearer " + localStorage.getItem("token") } }
      );

      if (res.data?.success) {
        const arr = Array.isArray(res.data.data) ? res.data.data : [];
        arr.sort(
          (a, b) =>
            new Date(b?.orderDate || 0).getTime() -
            new Date(a?.orderDate || 0).getTime()
        );
        setAllOrders(arr);
      } else {
        setAllOrders([]);
      }
    } catch (err) {
      console.error(err);
      setAllOrders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.email) getAllUserOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email]);

  const recentOrders = useMemo(() => allOrders.slice(0, 8), [allOrders]);

  const totalPurchase = useMemo(
    () =>
      allOrders.reduce((sum, order) => {
        const amount = Number(order?.amount);
        return Number.isFinite(amount) ? sum + amount : sum;
      }, 0),
    [allOrders]
  );

  const formatDateTime = (d) => {
    if (!d) return "-";
    const dt = new Date(d);
    if (Number.isNaN(dt.getTime())) return "-";
    return dt.toLocaleString("default", {
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const formatCurrency = (value) =>
    `₹${Number(value || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })}`;

  const userName = String(user?.fname || "").trim() || "User";
  const userEmail = String(user?.email || "").trim();
  const walletBalance = Number(user?.balance) || 0;

  const resellerExpiry = user?.resellerExpiry ? new Date(user.resellerExpiry) : null;
  const resellerActive = user?.reseller === "yes" && (!resellerExpiry || resellerExpiry > new Date());
  const resellerLabel  = resellerActive
    ? (resellerExpiry
        ? `Active · Exp ${resellerExpiry.toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}`
        : "Active · Permanent")
    : "Inactive";

  const stats = [
    {
      label: "Total Orders",
      value: loading ? "..." : allOrders.length,
      icon: <ReceiptLongIcon />,
      onClick: () => navigate("/orders"),
    },
    {
      label: "Total Purchase",
      value: loading ? "..." : formatCurrency(totalPurchase),
      icon: <CreditCardIcon />,
    },
    {
      label: "Wallet Balance",
      value: formatCurrency(walletBalance),
      icon: <AccountBalanceWalletIcon />,
      onClick: () => navigate("/add-balance"),
    },
    {
      label: "Reseller Status",
      value: resellerLabel,
      icon: <WorkspacePremiumIcon />,
      onClick: () => navigate("/subscription"),
    },
    {
      label: "Global Rank",
      value: "0",
      icon: <StarBorderIcon />,
      onClick: () => navigate("/leaderboard"),
    },
  ];

  return (
    <Layout>
      <DashboardLayout>
        <section className="dashWelcome">
          <h2>Hello, {userName}</h2>
          <p>Track your orders, wallet balance and recent activity easily.</p>
          {userEmail && <span>{userEmail}</span>}
        </section>

        <div className="dashStatsGrid">
          {stats.map((stat) => (
            <button
              type="button"
              key={stat.label}
              className="dashStatCard"
              onClick={stat.onClick}
              disabled={!stat.onClick}
            >
              <span className="dashStatIcon">{stat.icon}</span>
              <strong>{stat.value}</strong>
              <span>{stat.label}</span>
            </button>
          ))}
        </div>

        <section className="dashSection">
          <div className="dashSectionHeader">
            <h3 className="dashTitle">Recent Transactions</h3>
            <button className="dashViewAll" type="button" onClick={() => navigate("/orders")}>
              View All
            </button>
          </div>

          {loading ? (
            <div className="loaderWrap">
              <span className="loader" />
            </div>
          ) : recentOrders.length === 0 ? (
            <div className="noOrderDash">
              <b>No recent transactions found</b>
              <button className="dashLinkBtn" type="button" onClick={() => navigate("/")}>
                Go to Shop
              </button>
            </div>
          ) : (
            <div className="dashTableWrap">
              <table className="dashTable">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Type</th>
                    <th>Order ID</th>
                    <th>Product</th>
                    <th>Amount</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recentOrders.map((item, index) => {
                    const isExplore = item?._type === "explore";

                    return (
                      <tr key={item?.orderId || index}>
                        <td>{formatDateTime(item?.orderDate)}</td>
                        <td>
                          <span className="orderTypeBadge explore">
                            {isExplore ? "service" : "order"}
                          </span>
                        </td>
                        <td className="mono">#{item?.orderId || "-"}</td>
                        <td>{item?.product_name || item?.pack || "-"}</td>
                        <td className="mono">{item?.amount ? formatCurrency(item.amount) : "-"}</td>
                        <td>
                          <span className={`statusPill ${String(item?.status || "").toLowerCase()}`}>
                            {item?.status || "-"}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {allOrders.length > recentOrders.length && (
                <div className="dashHint">
                  Showing latest {recentOrders.length} of {allOrders.length} transactions.{" "}
                  <button className="dashLinkBtn" type="button" onClick={() => navigate("/orders")}>
                    View all
                  </button>
                </div>
              )}
            </div>
          )}
        </section>
      </DashboardLayout>
    </Layout>
  );
};

export default Dashboard;
